import { Page } from '@fe-learner-common/Page';
import { ScrollRestoration } from 'react-router-dom';
import { NextGenUiToggle } from '@components/NextGenUiToggle';
import { useIntl } from 'react-intl';
import { CourseCardGrid } from '@routes/catalog/components/CourseCardGrid';
import { CourseCard } from '@routes/catalog/components/Cards/CourseCard';
import { CollectionCard } from '@routes/catalog/components/Cards/CollectionCard';
import { SkeletonWrapper, Sorter, TSortOption } from '@fe-learner-common';
import { Categories } from '@routes/catalog/components/Categories/Categories';
import { useSelectedCategoryParam } from '@routes/catalog/components/Categories/useSelectedCategoryParam';
import { Breadcrumb } from '@fe-learner-common/components/Breadcrumb';
import { useCategoryBreadcrumbs } from '@routes/catalog/components/Categories/useCategoryBreadcrumbs';
import { useFilterCategoryData } from '@hooks/useFilterCategoryData';
import { getBundleSessions, useSessionsData } from './hooks/useSessionsData';
import { TW_ROOT } from '@utils/constants';
import { useState } from 'react';
import { SortOrder } from '@utils/sortSessions';
import { EmptyState } from '@fe-learner-common/components/EmptyState';
import { useApplyPageConfig } from '@routes/useApplyPageConfig';
import { FilterView } from '@routes/catalog/components/Filter/Filter';
import { useCatalogFilter } from './hooks/useCatalogFilter';

export const CatalogPage = () => {
  useApplyPageConfig();

  const intl = useIntl();
  const selectedCategoryId = useSelectedCategoryParam();
  const sortOptions = [
    {
      key: 'name-asc',
      label: intl.formatMessage({ id: 'learner.common.sortBy.name.asc' }),
    },
    {
      key: 'name-desc',
      label: intl.formatMessage({ id: 'learner.common.sortBy.name.desc' }),
    },
  ];

  const [sortOrder, setSortOrder] = useState<TSortOption>(sortOptions[0]);
  const { sessionData, isFetching, error } = useSessionsData();

  const { categoryData } = useFilterCategoryData({
    selectedCategoryId,
  });

  const { filterConfig, filter, setFilter, clearFilter, filteredItems } =
    useCatalogFilter({
      sessionData,
      category: selectedCategoryId,
      sortOrder: sortOrder.key as SortOrder,
    });

  const breadcrumbItems = useCategoryBreadcrumbs({ categoryData });

  const handleSortOrderChange = (sortOrder: TSortOption) => {
    setSortOrder(sortOrder);
  };

  if (error) {
    throw error;
  }

  return (
    <>
      <NextGenUiToggle isLoading={false} />
      <Page>
        <ScrollRestoration />
        <div className="lf:md:space-y-3">
          <div className="lf:space-y-2 lf:px-3">
            <Breadcrumb
              rootTitle={intl.formatMessage({ id: 'catalog.directory.title' })}
              items={breadcrumbItems}
            />
            <div className="lf:flex lf:flex-wrap lf:items-center lf:justify-between">
              <h1 className="lf:text-headline-lg">
                {intl.formatMessage({ id: 'catalog.directory.title' })}
              </h1>
              <div className="lf:flex lf:flex-wrap lf:items-center lf:gap-3">
                <Sorter
                  container={document.getElementById(TW_ROOT) as HTMLElement}
                  items={sortOptions}
                  onSelect={handleSortOrderChange}
                  selectedSortOrderKey={sortOrder.key}
                />
                <FilterView
                  filter={filter}
                  filterConfig={filterConfig}
                  setFilter={setFilter}
                  clearFilter={clearFilter}
                  nrOfResults={filteredItems?.length}
                />
                <SkeletonWrapper
                  className="lf:ms-auto"
                  isLoading={isFetching}
                  type="title"
                >
                  <span className=" lf:text-body-md lf:text-neutral-500">
                    <span className="lf:hidden lf:text-nowrap lf:md:inline">
                      {intl.formatMessage(
                        { id: 'learner.common.results' },
                        { count: filteredItems?.length },
                      )}
                    </span>
                    <span className="lf:text-nowrap lf:md:hidden">
                      {intl.formatMessage(
                        { id: 'learner.common.results.short' },
                        { count: filteredItems?.length },
                      )}
                    </span>
                  </span>
                </SkeletonWrapper>
              </div>
            </div>
          </div>

          <Categories categoryData={categoryData} />

          <section>
            {!filteredItems?.length && !isFetching ? (
              <div className="lf:p-10">
                <EmptyState
                  label={intl.formatMessage({
                    id: 'catalog.directory.empty.label',
                  })}
                  description={intl.formatMessage({
                    id: 'catalog.directory.empty.description',
                  })}
                />
              </div>
            ) : (
              <CourseCardGrid>
                {filteredItems?.map((session) =>
                  session.type === 'session' ? (
                    <CourseCard key={session.uuid} session={session} />
                  ) : (
                    <CollectionCard
                      key={session.uuid}
                      bundle={session}
                      sessions={getBundleSessions(
                        session,
                        sessionData?.sessionMap,
                      )}
                    />
                  ),
                )}
              </CourseCardGrid>
            )}
          </section>
        </div>
      </Page>
    </>
  );
};
